/**
 * Event types for the per-user notification stream (`/message/sse/users/me`).
 *
 * Unlike the closeout stream, these events carry no CloudEvent envelope: the type is the SSE
 * event name and `data` is the raw payload, so the envelope is assembled in
 * `AMSService.streamUserEvents`.
 */

import { TradingAccountStatusesEnum } from '../../models/crm/types';
import { NullableNumber } from '../../util/types';

export const USER_EVENT_TYPES = {
  // Keep-alive frame - carries a timestamp, not a payload, and is dropped before parsing
  HEARTBEAT: 'heartbeat',
  // A new trading account was created for the user
  ACCOUNT_CREATED: 'AccountCreated',
  // The status of one of the user's trading accounts changed
  ACCOUNT_STATUS_CHANGED: 'AccountStatusChanged',
} as const;

// Fields shared by the account events. Only what is consumed is declared - the server sends a larger payload
export interface AccountEventBaseData {
  accountId: number;
  accountReferenceId: string;
  /** Null for cash accounts, which are created without a trading plan. */
  tradingPlanId: NullableNumber;
}

export type AccountCreatedData = AccountEventBaseData;

/** Envelope shared by every event - `id` is the SSE frame id, e.g. "1790684539655-0". */
export interface BaseUserEvent {
  id: string;
}

export interface AccountCreatedEvent extends BaseUserEvent {
  type: typeof USER_EVENT_TYPES.ACCOUNT_CREATED;
  data: AccountCreatedData;
}

export interface AccountStatusChangedData extends AccountEventBaseData {
  previousStatus: TradingAccountStatusesEnum;
  status: TradingAccountStatusesEnum;
}

export interface AccountStatusChangedEvent extends BaseUserEvent {
  type: typeof USER_EVENT_TYPES.ACCOUNT_STATUS_CHANGED;
  data: AccountStatusChangedData;
}

/** Fallback for event names that are not typed yet. */
export interface UnknownUserEvent extends BaseUserEvent {
  type: string;
  data: unknown;
}

export type UserSSEEvent =
  | AccountCreatedEvent
  | AccountStatusChangedEvent
  | UnknownUserEvent;

export function isAccountCreatedEvent(event: UserSSEEvent): event is AccountCreatedEvent {
  return event.type === USER_EVENT_TYPES.ACCOUNT_CREATED;
}

export function isAccountStatusChangedEvent(event: UserSSEEvent): event is AccountStatusChangedEvent {
  return event.type === USER_EVENT_TYPES.ACCOUNT_STATUS_CHANGED;
}

/** What is kept in the store - the frame id plus the fields we use. The state key carries the type. */
export interface StoredUserEvent<TData> {
  id: string;
  data: TData;
}

/**
 * The parsed payload keeps every field the server sent, so it has to be projected explicitly
 * before it is stored - the interfaces above only narrow what TypeScript shows.
 */
export function toAccountCreatedData(data: AccountCreatedData): AccountCreatedData {
  const { accountId, accountReferenceId, tradingPlanId } = data;

  return { accountId, accountReferenceId, tradingPlanId };
}

export function toAccountStatusChangedData(data: AccountStatusChangedData): AccountStatusChangedData {
  const { accountId, accountReferenceId, tradingPlanId, previousStatus, status } = data;

  return { accountId, accountReferenceId, tradingPlanId, previousStatus, status };
}

export function toStoredAccountCreated(event: AccountCreatedEvent): StoredUserEvent<AccountCreatedData> {
  return { id: event.id, data: toAccountCreatedData(event.data) };
}

export function toStoredAccountStatusChanged(
  event: AccountStatusChangedEvent,
): StoredUserEvent<AccountStatusChangedData> {
  return { id: event.id, data: toAccountStatusChangedData(event.data) };
}

/**
 * Same stripping for the persisted copy, but `type` is kept: localStorage holds whichever event
 * came last under a single key, so the envelope has to say what it is.
 * An event type we do not model yet is stored as it came - there is nothing to project it against.
 */
export function toPersistedUserEvent(event: UserSSEEvent): UserSSEEvent {
  if (isAccountCreatedEvent(event)) {
    return { id: event.id, type: event.type, data: toAccountCreatedData(event.data) };
  }

  if (isAccountStatusChangedEvent(event)) {
    return { id: event.id, type: event.type, data: toAccountStatusChangedData(event.data) };
  }

  return event;
}

/**
 * Cash accounts are created without a trading plan, so a missing `tradingPlanId` is what marks them.
 * Nothing waits on a cash account - they reach the store with the accounts call.
 */
export function isCashAccountEvent(event: AccountCreatedEvent | AccountStatusChangedEvent): boolean {
  return event.data.tradingPlanId == null;
}

/** Any event carrying the shared account payload, so they can be filtered in one place. */
export function isAccountEvent(event: UserSSEEvent): event is AccountCreatedEvent | AccountStatusChangedEvent {
  return isAccountCreatedEvent(event) || isAccountStatusChangedEvent(event);
}

/** Callbacks the user events stream reports through. Grouped so the call stays one argument. */
export type UserEventsStreamHandlers = {
  onEvent: (event: UserSSEEvent) => void;
  /** Fires when the server accepts the stream, before any event arrives. */
  onOpen?: () => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
};
